import {
  Configuration,
  IPublicClientApplication,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { AUTH_ENABLED, getScopes } from "./authConstants";
import { useMsal } from "@azure/msal-react";
import {
  CONFIG_URL,
  API_VERSION,
  PRPV_PARAM,
  GEOCATALOGNAME_PARAM,
} from "utils/constants";
import { IAuthConfig } from "types";
import { useQuery } from "react-query";

export const useAuthConfig = () => {
  return useQuery("authConfig", getAuthConfig);
};

export const getAuthConfig = async (): Promise<IAuthConfig> => {
  const response = await fetch(`${CONFIG_URL}/auth?api-version=${API_VERSION}`);
  return await response.json();
};

export const getMsalApplication = async () => {
  if (!AUTH_ENABLED) {
    const config = getMsalConfig("unset", "unset", "unset", "unset");
    return new PublicClientApplication(config);
  }

  const { authn_app_id, tenant_id, instance, redirect_uri } = await getAuthConfig();

  const appConfig = getMsalConfig(authn_app_id, tenant_id, instance, redirect_uri);
  const msalInstance = new PublicClientApplication(appConfig);

  return msalInstance;
};

export const getMsalConfig = (
  appId: string,
  tenantId: string,
  instance: string,
  redirect_uri: string
): Configuration => {
  //if instance ends with a slash, remove it
  if (instance.endsWith("/")) {
    instance = instance.slice(0, -1);
  }
  return {
    auth: {
      clientId: appId,
      authority: `${instance}/${tenantId}`,
      redirectUri: redirect_uri || new URL(window.location.href).origin,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
  };
};

// Scopes for initial ID and Access token request, including consents for all scopes.
export const useLoginRequest = (): RedirectRequest => {
  const msal = useMsal();
  const config = msal.instance.getConfiguration();
  const scopes = getScopes(config.auth.clientId);

  // the url is of the form https://tc-demo.bxfqdqh5dagmbgez.uksouth.geocatalog.spatio.azure.com/
  // we want to extract the first three parts (tc-demo.bxfqdqh5dagmbgez.uksouth) and use that as the state
  // to redirect back to after login
  const redirectUri = new URL(window.location.href);
  // extract geocatalogname, namespace, region from the url parameters
  const params = redirectUri.searchParams.get(GEOCATALOGNAME_PARAM);
  // if PrvRP we send it in the state as well, so that we can validate it on the way back
  const prvPRState = redirectUri.searchParams.get(PRPV_PARAM);
  const [geoCatalogName, namespace, region] = params?.split(".") ?? ["", "", ""];
  let state = `${geoCatalogName}.${namespace}.${region}`;
  if (prvPRState) {
    state = state.concat(`|${prvPRState}`);
  }
  return {
    scopes: [scopes.default],
    state: state,
  };
};
// Scopes that are required for the API gateway request. Scopes here need to have
// been consented to in login for them to be requested silently later.
export const getTokenRequest = (
  instance: IPublicClientApplication
): SilentRequest => {
  const clientId = instance.getConfiguration().auth.clientId;
  const scopes = getScopes(clientId);
  return {
    scopes: [scopes.default],
  };
};

export const graphTokenRequest: SilentRequest = {
  scopes: ["User.Read"],
};
